import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Loader2, Plus, Eye, Trash2, UserCheck, Mail, Phone, Calendar, Handshake, Link as LinkIcon, MapPin, Globe, X } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../../context/WorkspaceContext';
import { crmAPI } from '../../lib/crm';

export default function CrmLeadsPage() {
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [partnerships, setPartnerships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [partnershipId, setPartnershipId] = useState('');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const [lres, pres] = await Promise.all([
                crmAPI.listLeads(currentWorkspace.id),
                crmAPI.listPartnerships(currentWorkspace.id),
            ]);
            setRows(lres.data || []);
            setPartnerships(pres.data || []);
        } catch (e) {
            toast.error('Gagal memuat Leads');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => { load(); }, [load]);

    // Available partnerships = those NOT yet in leads
    const leadPartnershipIds = new Set(rows.map((r) => r.partnership_id));
    const availablePartnerships = partnerships.filter((p) => !leadPartnershipIds.has(p.id));

    const save = async () => {
        if (!partnershipId) return toast.error('Pilih Contact terlebih dahulu');
        try {
            setSaving(true);
            await crmAPI.createLead({
                workspace_id: currentWorkspace.id,
                partnership_id: partnershipId,
                date,
            });
            toast.success('Lead dibuat');
            setFormOpen(false);
            setPartnershipId('');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Hapus lead "${row.partnership?.company_name}"?`)) return;
        try {
            await crmAPI.deleteLead(row.id);
            toast.success('Dihapus');
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    return (
        <Layout title="Lead" subtitle="Calon klien yang telah melakukan first conversation">
            <div className="flex justify-end mb-4">
                <Button onClick={() => setFormOpen(true)} data-testid="lead-create-btn">
                    <Plus className="w-4 h-4 mr-2" /> Promosikan Contact jadi Lead
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                </div>
            ) : rows.length === 0 ? (
                <div className="text-center py-20 text-brand-text-muted">
                    <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Belum ada Lead</p>
                </div>
            ) : (
                <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Perusahaan</TableHead>
                                <TableHead>PIC</TableHead>
                                <TableHead>Position</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Last Contact</TableHead>
                                <TableHead>Next Contact</TableHead>
                                <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`lead-row-${r.id}`}>
                                    <TableCell className="font-medium">{r.partnership?.company_name || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_name || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_title || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_email || '-'}</TableCell>
                                    <TableCell>{r.partnership?.pic_phone || '-'}</TableCell>
                                    <TableCell>{r.partnership?.lead_last_contact || '-'}</TableCell>
                                    <TableCell>{r.partnership?.lead_next_contact || '-'}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="icon" onClick={async () => {
                                                const res = await crmAPI.getLead(r.id);
                                                setDetail(res.data);
                                            }} data-testid={`lead-detail-${r.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`lead-delete-${r.id}`}>
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Promosikan Contact jadi Lead</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Pilih Contact *</Label>
                            <Select value={partnershipId} onValueChange={setPartnershipId}>
                                <SelectTrigger data-testid="lead-partnership-select"><SelectValue placeholder="Pilih perusahaan..." /></SelectTrigger>
                                <SelectContent>
                                    {availablePartnerships.length === 0 ? (
                                        <div className="px-2 py-3 text-sm text-brand-text-muted">Semua contact sudah menjadi lead. Tambah contact baru dulu.</div>
                                    ) : availablePartnerships.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Tanggal menjadi Lead *</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="lead-date" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
                        <Button onClick={save} disabled={saving} data-testid="lead-save-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail">
                    {detail && <LeadDetail lead={detail} onReload={async () => {
                        const res = await crmAPI.getLead(detail.id);
                        setDetail(res.data);
                    }} />}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function LeadDetail({ lead, onReload }) {
    const p = lead.partnership || {};
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [nextContact, setNextContact] = useState('');
    const [content, setContent] = useState('');
    const [saving, setSaving] = useState(false);

    const addContact = async () => {
        if (!content.trim()) return toast.error('Isi wajib diisi');
        try {
            setSaving(true);
            const payload = { date, content, stage: 'lead' };
            if (nextContact) payload.next_contact = nextContact;
            await crmAPI.addContact(p.id, payload);
            setContent('');
            setNextContact('');
            toast.success('Kontak ditambahkan');
            await onReload();
        } catch (e) {
            toast.error('Gagal');
        } finally {
            setSaving(false);
        }
    };

    const removeContact = async (cid) => {
        if (!window.confirm('Hapus kontak ini?')) return;
        try { await crmAPI.deleteContact(cid); await onReload(); }
        catch (e) { toast.error('Gagal'); }
    };

    return (
        <div>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-brand-primary" /> {p.company_name} <span className="text-xs text-brand-text-muted">— Lead sejak {lead.date}</span>
                </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <Info icon={Handshake} label="PIC" value={`${p.pic_name || '-'} — ${p.pic_title || ''}`} />
                    <Info icon={Mail} label="Email" value={p.pic_email} />
                    <Info icon={Phone} label="Phone" value={p.pic_phone} />
                    <Info icon={LinkIcon} label="LinkedIn" value={p.pic_linkedin} href={p.pic_linkedin} />
                    <Info icon={Globe} label="Website" value={p.company_website} href={p.company_website} />
                    <Info icon={MapPin} label="Alamat" value={p.company_address} />
                    <Info icon={Calendar} label="Last Contact (saat Lead)" value={p.lead_last_contact} />
                    <Info icon={Calendar} label="Next Contact (saat Lead)" value={p.lead_next_contact} />
                </div>

                <div className="border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Tambah Kontak (tahap Lead)</div>
                    <div className="flex gap-2 items-end flex-wrap">
                        <div><Label>Tanggal</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="lead-contact-date" /></div>
                        <div><Label>Next Contact</Label><Input type="date" value={nextContact} onChange={(e) => setNextContact(e.target.value)} className="w-40" data-testid="lead-contact-next-date" /></div>
                        <div className="flex-1 min-w-[200px]"><Label>Isi</Label><Input value={content} onChange={(e) => setContent(e.target.value)} data-testid="lead-contact-content" /></div>
                        <Button onClick={addContact} disabled={saving} data-testid="lead-contact-add-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</Button>
                    </div>
                </div>

                <div className="border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Riwayat Kontak (tahap Lead)</div>
                    {(p.lead_contacts || []).length === 0 ? <div className="text-sm text-brand-text-muted">Belum ada kontak tahap Lead</div> : (
                        <div className="space-y-2">
                            {p.lead_contacts.map((c) => (
                                <div key={c.id} className="flex items-start gap-3 p-3 bg-brand-accent-soft/30 rounded-lg">
                                    <Calendar className="w-4 h-4 mt-0.5 text-brand-primary" />
                                    <div className="flex-1">
                                        <div className="text-xs text-brand-text-muted">{c.date}</div>
                                        <div className="text-sm">{c.content}</div>
                                        {c.next_contact && (
                                            <div className="text-xs text-brand-primary mt-1">Next Contact: {c.next_contact}</div>
                                        )}
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => removeContact(c.id)}><X className="w-4 h-4" /></Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Riwayat Kontak (tahap Contact)</div>
                    {(p.partnership_contacts || []).length === 0 ? <div className="text-sm text-brand-text-muted">Belum ada kontak tahap Contact</div> : (
                        <div className="space-y-2">
                            {p.partnership_contacts.map((c) => (
                                <div key={c.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                                    <Calendar className="w-4 h-4 mt-0.5 text-brand-text-muted" />
                                    <div className="flex-1">
                                        <div className="text-xs text-brand-text-muted">{c.date}</div>
                                        <div className="text-sm">{c.content}</div>
                                        {c.next_contact && (
                                            <div className="text-xs text-brand-primary mt-1">Next Contact: {c.next_contact}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Info({ icon: Icon, label, value, href }) {
    return (
        <div className="flex items-start gap-2">
            <Icon className="w-4 h-4 mt-0.5 text-brand-text-muted flex-shrink-0" />
            <div>
                <div className="text-xs text-brand-text-muted">{label}</div>
                {href && value ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline break-all">{value}</a> : <div className="break-words">{value || '-'}</div>}
            </div>
        </div>
    );
}
